
import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, ChevronRight, CheckCircle, Circle, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export interface TopicItem {
  id: string;
  title: string;
  children?: TopicItem[];
  completed?: boolean;
  /** Optional route for a navigation item that is not an article lesson. */
  path?: string;
  /** Workspace/tool links should not affect lesson completion totals. */
  trackProgress?: boolean;
  /** Parent completion is calculated from its children and cannot be set manually. */
  derivedCompletion?: boolean;
}

export interface ContentSection {
  id: string;
  title: string;
  level: number; // 1 for h1, 2 for h2, etc.
}

interface PracticeContentLayoutProps {
  className?: string;
  title: string;
  totalChapters: number;
  completedChapters: number;
  topics: TopicItem[];
  currentTopicId: string;
  onTopicSelect: (topicId: string, path?: string) => void;
  onToggleComplete?: (topicId: string, completed: boolean) => void;
  content: React.ReactNode;
  onContentScroll?: (scrollTop: number) => void;
  rightSidebarContent?: React.ReactNode;
}

const PracticeContentLayout: React.FC<PracticeContentLayoutProps> = ({
  className = '',
  title,
  totalChapters,
  completedChapters,
  topics,
  currentTopicId,
  onTopicSelect,
  onToggleComplete,
  content,
  onContentScroll,
  rightSidebarContent,
}) => {
  useTheme(); // Theme context used for dark mode styling
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [contentSections, setContentSections] = useState<ContentSection[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string>('');
  const [readingProgress, setReadingProgress] = useState(0);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // The article itself is the primary surface. Keeping two fixed-width rails
  // open on a mid-size viewport leaves too little room for tables and system
  // diagrams, so begin with a readable canvas and retain the existing toggles
  // for anyone who wants a rail back.
  useEffect(() => {
    const setResponsiveRails = () => {
      setIsRightSidebarOpen(window.innerWidth >= 1280);
      setIsLeftSidebarOpen(window.innerWidth >= 900);
    };

    setResponsiveRails();
    window.addEventListener('resize', setResponsiveRails);
    return () => window.removeEventListener('resize', setResponsiveRails);
  }, []);

  // Initialize expanded topics (expand current topic's path)
  useEffect(() => {
    const expandPath = (items: TopicItem[], targetId: string, path: Set<string>): boolean => {
      for (const item of items) {
        if (item.id === targetId) {
          return true;
        }
        if (item.children) {
          if (expandPath(item.children, targetId, path)) {
            path.add(item.id);
            return true;
          }
        }
      }
      return false;
    };

    const path = new Set<string>();
    expandPath(topics, currentTopicId, path);
    setExpandedTopics(path);
  }, [currentTopicId, topics]);

  // Scroll the content area back to the top whenever the topic changes.
  // Lesson content is LAZY-LOADED, so the real (tall) article can mount a few
  // hundred ms AFTER the route changes. If we only reset once up-front (while
  // the Suspense fallback is showing), the browser's scroll anchoring can leave
  // the view parked mid-article (e.g. on "1. The Real Problem") once the real
  // content injects. So we reset immediately, on the next frame, and a few
  // times over the following moments to catch the post-lazy-load layout.
  useEffect(() => {
    const toTop = () => {
      if (contentRef.current) contentRef.current.scrollTop = 0;
    };
    toTop();
    setReadingProgress(0);
    const raf = requestAnimationFrame(toTop);
    const timers = [60, 200, 500].map((ms) => setTimeout(toTop, ms));
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [currentTopicId]);

  // Extract content sections from headings
  useEffect(() => {
    if (!contentRef.current) return;

    // Clear previous sections immediately on topic change so the right sidebar
    // doesn't show stale headings from the previous page.
    setContentSections([]);
    setActiveSectionId('');

    const extractHeadings = () => {
      if (!contentRef.current) return;

      const allHeadings = Array.from(contentRef.current.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6'));
      const numberedHeadings = allHeadings.filter((heading) => heading.dataset.tocTitle);
      // Numbered articles use their explicit section headings as the TOC.
      // This prevents definition labels (for example Scalability/Availability)
      // from being mistaken for navigational sections.
      const headings = numberedHeadings.length > 0 ? numberedHeadings : allHeadings;
      const sections: ContentSection[] = [];

      headings.forEach((heading, index) => {
        const id = `section-${index}`;
        heading.id = id;
        const level = parseInt(heading.tagName.charAt(1));
        sections.push({
          id,
          title: heading.dataset.tocTitle || heading.textContent || '',
          level,
        });
      });

      setContentSections(sections);

      // Set up intersection observer for reading progress
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSectionId(entry.target.id);
            }
          });
        },
        {
          rootMargin: '-20% 0px -60% 0px',
          threshold: 0,
        }
      );

      headings.forEach((heading) => {
        observerRef.current?.observe(heading);
      });
    };

    // Content is loaded asynchronously (DynamicContentPage fetches from API).
    // Run extraction after a short delay to let the new content render, and
    // re-check a few times to catch late-loading content.
    const timers = [100, 400, 800, 1500].map((ms) => setTimeout(extractHeadings, ms));

    return () => {
      observerRef.current?.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [content, currentTopicId]);

  // Calculate reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const element = contentRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight - element.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setReadingProgress(Math.min(100, Math.max(0, progress)));

      if (onContentScroll) {
        onContentScroll(scrollTop);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [onContentScroll]);

  const toggleTopic = (topicId: string) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  const filteredTopics = (items: TopicItem[]): TopicItem[] => {
    if (!searchTerm) return items;

    const result: TopicItem[] = [];

    for (const item of items) {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
      const filteredChildren = item.children ? filteredTopics(item.children) : [];

      if (matchesSearch || filteredChildren.length > 0) {
        result.push({
          ...item,
          children: filteredChildren.length > 0 ? filteredChildren : item.children,
        });
      }
    }

    return result;
  };

  const renderTopicItem = (item: TopicItem, level: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedTopics.has(item.id);
    const isActive = item.id === currentTopicId;
    const isCompleted = item.completed;

    const handleClick = () => {
      if (hasChildren) {
        toggleTopic(item.id);
      } else {
        onTopicSelect(item.id, item.path);
      }
    };

    return (
      <div key={item.id}>
        <div
          onClick={handleClick}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors
            ${isActive
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
              : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
            }
            ${level > 0 ? 'ml-4' : ''}
          `}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            )
          ) : (
            <div className="w-4 h-4 flex-shrink-0" />
          )}
          {item.trackProgress === false ? (
            <span className="w-4 shrink-0 text-center text-[10px] font-bold uppercase text-primary-600 dark:text-primary-400" aria-hidden="true">↗</span>
          ) : item.derivedCompletion ? (
            isCompleted
              ? <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-600 dark:text-green-400" aria-label="Complete" />
              : <Circle className="w-4 h-4 flex-shrink-0 opacity-50" aria-label="Incomplete" />
          ) : isCompleted ? (
            <span title="Mark for revision" onClick={(e) => { e.stopPropagation(); onToggleComplete?.(item.id, false); }}>
              <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-600 dark:text-green-400 cursor-pointer hover:text-green-800 dark:hover:text-green-200 transition-colors" />
            </span>
          ) : (
            <span title="Mark as complete" onClick={(e) => { e.stopPropagation(); onToggleComplete?.(item.id, true); }}>
              <Circle className="w-4 h-4 flex-shrink-0 opacity-50 cursor-pointer hover:opacity-100 hover:text-green-600 dark:hover:text-green-400 transition-colors" />
            </span>
          )}
          <span className="text-sm flex-1 truncate">{item.title}</span>
        </div>
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {item.children!.map((child) => renderTopicItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element && contentRef.current) {
      const offset = 100; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + contentRef.current.scrollTop - offset;

      contentRef.current.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const progressPercentage = totalChapters > 0 ? (completedChapters / totalChapters) * 100 : 0;

  return (
    <div className={`practice-content-layout flex h-[calc(100vh-4rem)] pt-16 bg-gray-50 dark:bg-gray-900 relative ${className}`}>
      {/* Left Sidebar */}
      <div className={`practice-content-sidebar practice-content-sidebar--left relative border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col transition-all duration-300 ${isLeftSidebarOpen ? 'w-80' : 'w-0'
        } ${isLeftSidebarOpen ? 'overflow-hidden' : 'overflow-visible'}`}>
        {/* Left Sidebar Toggle Button */}
        <button
          onClick={() => setIsLeftSidebarOpen(!isLeftSidebarOpen)}
          className={`practice-content-toggle absolute top-4 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1.5 shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 ${isLeftSidebarOpen ? '-right-3' : 'left-2'
            }`}
          aria-label="Toggle left sidebar"
        >
          {isLeftSidebarOpen ? (
            <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          )}
        </button>
        {/* Header */}
        <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${!isLeftSidebarOpen ? 'hidden' : ''}`}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {title}
          </h2>

          {/* Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600 dark:text-gray-400">Progress</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {completedChapters}/{totalChapters} chapters
              </span>
            </div>
            <div className="practice-progress-track w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="practice-progress-fill bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="practice-content-search w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Topics List */}
        <div className={`flex-1 overflow-y-auto p-2 ${!isLeftSidebarOpen ? 'hidden' : ''}`}>
          {filteredTopics(topics).map((topic) => renderTopicItem(topic))}
        </div>
      </div>

      {/* Middle Content */}
      <div
        ref={contentRef}
        data-content-area
        className="practice-content-main flex-1 overflow-y-auto bg-white dark:bg-gray-900"
        style={{ overflowAnchor: 'none' }}
      >
        <div className="practice-content-article max-w-4xl mx-auto px-8 py-8">
          {content}
        </div>
      </div>

      {/* Right Sidebar */}
      <div className={`practice-content-sidebar practice-content-sidebar--right relative border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col transition-all duration-300 ${isRightSidebarOpen ? 'w-80' : 'w-0'
        } ${isRightSidebarOpen ? 'overflow-hidden' : 'overflow-visible'}`}>
        {/* Right Sidebar Toggle Button */}
        <button
          onClick={() => setIsRightSidebarOpen(!isRightSidebarOpen)}
          className={`practice-content-toggle absolute top-4 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1.5 shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 ${isRightSidebarOpen ? '-left-3' : 'right-2'
            }`}
          aria-label="Toggle right sidebar"
        >
          {isRightSidebarOpen ? (
            <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          )}
        </button>
        {/* DISABLED: Premium Banner — uncomment when subscription feature is ready
        <div className={`p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 ${!isRightSidebarOpen ? 'hidden' : ''}`}>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Get Premium
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Subscribe to unlock full access to all premium content
          </p>
          <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm">
            Subscribe Now
          </button>
        </div>
        */}

        {/* Reading Progress */}
        <div className={`practice-reading-progress p-4 border-b border-gray-200 dark:border-gray-700 ${!isRightSidebarOpen ? 'hidden' : ''}`}>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Reading Progress
          </h3>
          <div className="practice-progress-track w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
            <div
              className="practice-progress-fill bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${readingProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {Math.round(readingProgress)}%
          </p>
        </div>

        {/* Table of Contents */}
        {contentSections.length > 0 && (
          <div className={`flex-1 overflow-y-auto p-4 ${!isRightSidebarOpen ? 'hidden' : ''}`}>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
              On this page
            </h3>
            <nav className="space-y-1">
              {contentSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`
                    block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors
                    ${activeSectionId === section.id
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                    ${section.level === 1 ? 'font-semibold' : ''}
                    ${section.level === 2 ? 'ml-4' : ''}
                    ${section.level === 3 ? 'ml-8' : ''}
                    ${section.level >= 4 ? 'ml-12' : ''}
                  `}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Custom Right Sidebar Content */}
        {rightSidebarContent && (
          <div className={`p-4 border-t border-gray-200 dark:border-gray-700 ${!isRightSidebarOpen ? 'hidden' : ''}`}>
            {rightSidebarContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default PracticeContentLayout;
